import { Navigate } from 'react-router-dom'
import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { useAuthStore } from '@/lib/auth'
import { usePermissions } from '@/lib/permissions'
import { apiFetch } from '@/lib/api-client'
import AppLayout from './app-layout'
import { SubscriptionExpired } from '@/components/subscription-expired'
import { useWallet } from '@/hooks/use-billing'
import { useDeadlineStatsServer } from '@/hooks/use-deadlines'
import { usePrefetchRoutes } from '@/hooks/use-prefetch-routes'
import { AppShellSkeleton } from '@/components/app-shell-skeleton'
import type { SubscriptionResponse } from '@aegis/zod-schemas'


type SubGuardState = 'loading' | 'active' | 'inactive'

export function AuthenticatedLayout() {
  usePrefetchRoutes()
  const { isAuthenticated, fetchPermissions, fetchCurrentUser, user } = useAuthStore()
  const { permissions } = usePermissions()
  const canReadWallet = useMemo(
    () => permissions.some((p) => p.resource === 'wallet' && p.action === 'read'),
    [permissions],
  )
  const canManageSubscription = useMemo(
    () => permissions.some((p) => p.resource === 'subscription' && p.action === 'update'),
    [permissions],
  )
  // L'utente DEVE aver accettato la privacy prima di poter chiamare gli
  // endpoint domain/billing. Altrimenti la Lambda Authorizer (consent gate)
  // li blocca con 403 e api-client interpreta il 403 senza `code` come
  // auth-expiry → tenta refresh → ancora 403 → clearAuth → /login (loop).
  // Tutte le query autenticate gated su questo flag.
  const hasAcceptedPrivacy = !!user?.privacyAcceptedAt
  const { data: wallet } = useWallet(canReadWallet && hasAcceptedPrivacy)
  const [subGuard, setSubGuard] = useState<SubGuardState>('loading')
  const [hasExistingSubscription, setHasExistingSubscription] = useState(false)
  // Skip /deadlines/stats while the sub guard is still resolving or inactive —
  // the endpoint requires an active subscription and would 403 noisily on
  // the reactivate/expired flows. Gated ANCHE su hasAcceptedPrivacy perché
  // il safety timeout (sotto, 5s) può forzare subGuard='active' anche se
  // il privacy gate non è stato superato; senza questo, /deadlines/stats
  // partirebbe e finirebbe in 403 dal consent gate → clearAuth loop.
  const { data: deadlineStats } = useDeadlineStatsServer(subGuard === 'active' && hasAcceptedPrivacy)
  const overdueCount = deadlineStats?.overdue ?? 0
  const upcomingCount = deadlineStats?.next7 ?? 0

  // Fetch permissions if empty (e.g. after page refresh — store is in-memory)
  useEffect(() => {
    if (isAuthenticated && permissions.length === 0) {
      fetchPermissions()
    }
  }, [isAuthenticated, permissions.length, fetchPermissions])

  useEffect(() => {
    if (isAuthenticated) {
      fetchCurrentUser().catch(() => {})
    }
  }, [isAuthenticated, fetchCurrentUser])

  // Safety timeout: if permissions never arrive (fetch swallows errors),
  // allow through after 5s rather than showing a blank screen forever.
  // Backend RBAC still enforces access control. Skip se privacy non è
  // stata accettata: in quello stato il guard sopra ha già fatto Navigate
  // a /accept-terms e non vogliamo che il timeout abiliti chiamate downstream
  // (es. /deadlines/stats) prima del consent.
  useEffect(() => {
    if (subGuard !== 'loading') return
    if (!hasAcceptedPrivacy) return
    const timer = setTimeout(() => {
      setSubGuard((prev) => prev === 'loading' ? 'active' : prev)
    }, 5000)
    return () => clearTimeout(timer)
  }, [subGuard, hasAcceptedPrivacy])

  // Subscription guard: check status, sync with Stripe on first load
  const syncedRef = useRef(false)
  const checkSubscription = useCallback(async () => {
    if (permissions.length === 0) return
    const canRead = permissions.some((p) => p.resource === 'subscription' && p.action === 'read')
    if (!canRead) {
      setSubGuard('active')
      return
    }
    try {
      // Sync with Stripe once per session — only if admin (sync requires subscription:update)
      const canUpdate = permissions.some((p) => p.resource === 'subscription' && p.action === 'update')
      if (!syncedRef.current && canUpdate) {
        syncedRef.current = true
        await apiFetch<SubscriptionResponse | null>('/payments/subscription/sync', { method: 'POST' }).catch(() => {})
      }
      const sub = await apiFetch<SubscriptionResponse | null>('/payments/subscription')
      const status = sub?.status ?? null
      setHasExistingSubscription(sub != null)
      setSubGuard(status === 'ACTIVE' || status === 'TRIALING' ? 'active' : 'inactive')
    } catch {
      setSubGuard('inactive')
    }
  }, [permissions])

  useEffect(() => {
    // Defer fino a quando la privacy non è accettata: il consent gate
    // della Lambda blocca /api/payments/* per utenti con
    // privacyAcceptedAt=null, e il 403 senza code triggera clearAuth.
    if (isAuthenticated && hasAcceptedPrivacy) {
      checkSubscription()
    }
  }, [isAuthenticated, hasAcceptedPrivacy, checkSubscription])

  const walletTotal = wallet != null
    ? wallet.subscriptionBalance + wallet.purchasedBalance
    : undefined

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Privacy gate (a 3 stati):
  //  - `user === null`            → fetchCurrentUser in volo. Stato
  //    UNKNOWN: NON sappiamo ancora se la privacy è accettata. Blocchiamo
  //    il render delle route figlie per evitare che le loro query partano
  //    e vengano bloccate dal consent gate del Lambda (403 → clearAuth loop).
  //  - `user.privacyAcceptedAt`   → privacy accettata, procediamo.
  //  - `!user.privacyAcceptedAt`  → utente esistente ma non ha mai accettato
  //    (es. invitato al primo login). Navigate al gate /accept-terms.
  // /accept-terms vive FUORI da AuthenticatedLayout (route auth-free in
  // router.tsx), quindi non serve eccezione sul pathname.
  const privacyKnown = user !== null
  if (!privacyKnown) {
    // fetchCurrentUser in volo: skeleton invece di sfondo grigio vuoto.
    return <AppShellSkeleton />
  }
  if (!hasAcceptedPrivacy) {
    return <Navigate to="/accept-terms" replace />
  }

  // Wait for subscription check to complete (privacy ormai accettata).
  if (subGuard === 'loading') {
    return <AppShellSkeleton />
  }

  // Subscription guard
  if (subGuard === 'inactive') {
    if (canManageSubscription) {
      // Distinguish first-time activation vs reactivation
      const param = hasExistingSubscription ? 'reactivate' : 'activate'
      return <Navigate to={`/billing/reactivate?mode=${param}`} replace />
    }
    return <SubscriptionExpired />
  }

  return (
    <AppLayout
      walletTotal={walletTotal}
      overdueCount={overdueCount}
      upcomingCount={upcomingCount}
    />
  )
}
